import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { CSS_RESTRICTIONS } from '@common/constants';
import type { GeneralSettingsFormGroup } from '@web/widgets/product-card/type';
import { type GeneralSettingsFormType } from '@web/widgets/product-card/type';
import { ThemeStateService } from '@sites/site-theme/src/state';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { ELineStyle } from '@common/enums';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SpSwitchComponent } from '@ui/sp-switch';
import { CommonModule } from '@angular/common';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpColorPickerModule } from '@components/sp-color-picker/src/sp-color-picker.module';
import type { AdvancedBorderSettings } from '@components/sp-advanced-border-settings/type/advanced-border-settings.type';
import { SpAdvancedBorderSettingsComponent } from '@components/sp-advanced-border-settings/sp-advanced-border-settings.component';

@Component({
    selector: 'web-general-settings',
    templateUrl: './general-settings.component.html',
    styleUrls: ['./general-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SpColorPickerModule,
        SpInputNumComponent,
        CommonModule,
        SpSwitchComponent,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        SpAdvancedBorderSettingsComponent,
    ],
})
export class GeneralSettingsComponent {
    private readonly themeState = inject(ThemeStateService);

    @Input() generalSettingsFormGroup: GeneralSettingsFormGroup;
    @Input() isModalSettings: boolean = false;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public setBorderRadius(value: number): void {
        this.generalSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.borderRadius.patchValue(value);
    }

    public setPadding(value: number): void {
        this.generalSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.padding.patchValue(value);
    }

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.generalSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setSubstrateColor({ value, presetName }: ColorPickerEventPayload): void {
        this.generalSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.substrateColor.patchValue(presetName ?? value);
    }

    public setAdvancedBorderSettings(updatedSettings: AdvancedBorderSettings): void {
        this.advancedBorderSettings.patchValue({ ...updatedSettings, isActive: this.isBorder.value });
    }

    public get advancedBorderSettingsValue(): AdvancedBorderSettings {
        if (this.advancedBorderSettings.value) {
            return this.advancedBorderSettings.value;
        }

        const width = +this.borderWidth.value || 1;
        const style = (this.borderStyle.value as ELineStyle) ?? ELineStyle.SOLID;
        const color = this.borderColor.value?.toString() ?? '#000000';

        return {
            isActive: this.isBorder.value,
            isIndependentBorders: false,
            borderTopWidth: width,
            borderTopColor: color,
            borderTopStyle: style,
            borderRightWidth: width,
            borderRightColor: color,
            borderRightStyle: style,
            borderBottomWidth: width,
            borderBottomColor: color,
            borderBottomStyle: style,
            borderLeftWidth: width,
            borderLeftColor: color,
            borderLeftStyle: style,
        };
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get PADDING_MIN(): number {
        return CSS_RESTRICTIONS.PADDING_MIN;
    }

    public get PADDING_MAX(): number {
        return CSS_RESTRICTIONS.PADDING_MAX;
    }

    public get backgroundColor(): GeneralSettingsFormType['backgroundColor'] {
        return this.generalSettingsFormGroup.get('backgroundColor') as GeneralSettingsFormType['backgroundColor'];
    }

    public get borderRadius(): GeneralSettingsFormType['borderRadius'] {
        return this.generalSettingsFormGroup.get('borderRadius') as GeneralSettingsFormType['borderRadius'];
    }

    public get padding(): GeneralSettingsFormType['padding'] {
        return this.generalSettingsFormGroup.get('padding') as GeneralSettingsFormType['padding'];
    }

    public get isBorder(): GeneralSettingsFormType['isBorder'] {
        return this.generalSettingsFormGroup.get('isBorder') as GeneralSettingsFormType['isBorder'];
    }

    public get borderWidth(): GeneralSettingsFormType['borderWidth'] {
        return this.generalSettingsFormGroup.get('borderWidth') as GeneralSettingsFormType['borderWidth'];
    }

    public get borderStyle(): GeneralSettingsFormType['borderStyle'] {
        return this.generalSettingsFormGroup.get('borderStyle') as GeneralSettingsFormType['borderStyle'];
    }

    public get borderColor(): GeneralSettingsFormType['borderColor'] {
        return this.generalSettingsFormGroup.get('borderColor') as GeneralSettingsFormType['borderColor'];
    }

    public get advancedBorderSettings(): GeneralSettingsFormType['advancedBorderSettings'] {
        return this.generalSettingsFormGroup.get(
            'advancedBorderSettings'
        ) as GeneralSettingsFormType['advancedBorderSettings'];
    }

    public get substrateColor(): GeneralSettingsFormType['substrateColor'] {
        return this.generalSettingsFormGroup.get('substrateColor') as GeneralSettingsFormType['substrateColor'];
    }
}
